/**
 * 通道面板（DESIGN.md §7.1 补充，参照 Photoshop）。
 *
 * 范围收在"查看/隔离 R/G/B 通道 + Cmd/Ctrl+点击转选区"——不做新建/复制/
 * 删除通道、专色通道、通道混合器（用户已明确选择不做，见完成报告）。
 * 跟 `LayersPanel` 同一个"数据进、回调出"的纯展示分层，不认识 store。
 */
import type { JSX } from 'solid-js';
import { For, Show } from 'solid-js';
import styles from './ChannelsPanel.module.css';

export type ChannelRowId = 'rgb' | 'r' | 'g' | 'b';

const ROWS: ReadonlyArray<{ readonly id: ChannelRowId; readonly label: string }> = [
  { id: 'rgb', label: 'RGB' },
  { id: 'r', label: '红' },
  { id: 'g', label: '绿' },
  { id: 'b', label: '蓝' },
];

export interface ChannelsPanelProps {
  /** 当前隔离查看的通道；'rgb' = 正常合成视图（缺省）。 */
  readonly activeChannel?: ChannelRowId | undefined;
  /** 点一行＝切换查看这个通道（再点 RGB 那行＝回到正常视图）。 */
  readonly onSelectChannel?: ((channel: ChannelRowId) => void) | undefined;
  /** Cmd/Ctrl+点击缩略图＝把该通道转成选区（PS 惯例，跟图层缩略图同一手势）。 */
  readonly onLoadSelectionFromChannel?:
    | ((channel: ChannelRowId, modifiers: { readonly shiftKey: boolean; readonly altKey: boolean }) => void)
    | undefined;
  /** 缩略图 data URL；没有文档或还没画出内容时返回 null。 */
  readonly thumbnail?: ((channel: ChannelRowId) => string | null) | undefined;
}

export function ChannelsPanel(props: ChannelsPanelProps): JSX.Element {
  const active = (): ChannelRowId => props.activeChannel ?? 'rgb';

  return (
    <div class={styles.panel} data-testid="channels-panel">
      <div class={styles.list} role="listbox" aria-label="通道">
        <For each={ROWS}>
          {(row) => (
            <div
              class={styles.row}
              classList={{ [styles.active!]: active() === row.id }}
              role="option"
              aria-selected={active() === row.id}
              data-testid={`channel-row-${row.id}`}
              onClick={() => props.onSelectChannel?.(row.id)}
            >
              <Show when={props.thumbnail?.(row.id) ?? null}>
                {(url) => (
                  <img
                    class={styles.thumb}
                    alt=""
                    src={url()}
                    data-testid={`channel-thumb-${row.id}`}
                    onClick={(event) => {
                      // Cmd/Ctrl+点击＝转选区，不当普通的切换查看点击
                      if (event.metaKey || event.ctrlKey) {
                        event.stopPropagation();
                        props.onLoadSelectionFromChannel?.(row.id, {
                          shiftKey: event.shiftKey,
                          altKey: event.altKey,
                        });
                      }
                    }}
                  />
                )}
              </Show>
              <span class={styles.name}>{row.label}</span>
            </div>
          )}
        </For>
      </div>
    </div>
  );
}
